import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Alert, Box, Button, Card, Stack, Tab, Tabs } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"

import { chatApi } from "../api"
import { usePermissions } from "../permissions"
import { useSocket } from "../context/SocketContext"
import { joinRoomTracked, leaveRoomTracked } from "../realtime/sharedSocket"
import PageHeader from "../components/PageHeader"
import { useFillViewport } from "../shell/useFillViewport"
import ClientSelect from "../components/ClientSelect"
import ChatRoomList from "./chat/ChatRoomList"
import ChatThread from "./chat/ChatThread"
import NewConversationDialog from "./chat/NewConversationDialog"

/**
 * Chat.
 *
 * Two panes: the inbox and the open thread. The inbox is the list of every
 * conversation you are in, across every client — which is the whole reason chat
 * lives in the platform database rather than in each tenant's schema.
 */
export default function ChatPage() {
  const { roomId } = useParams()
  const navigate = useNavigate()
  const { socket } = useSocket()
  const { can, isBackOffice } = usePermissions()

  const [rooms, setRooms] = useState([])
  const [filter, setFilter] = useState("all")
  const [clientFilter, setClientFilter] = useState("")
  const frameRef = useRef(null)
  const frameHeight = useFillViewport(frameRef)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [newOpen, setNewOpen] = useState(false)

  // Read inside socket handlers without making them re-subscribe on every change.
  const activeRoomRef = useRef(roomId)
  useEffect(() => {
    activeRoomRef.current = roomId
  }, [roomId])

  const loadRooms = useCallback(async () => {
    try {
      const data = await chatApi.rooms()
      setRooms(data.rooms || [])
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRooms()
  }, [loadRooms])

  /*
   * A message arriving for a thread the user is NOT looking at.
   *
   * `message_inbox` is delivered to the personal room, so it arrives whichever
   * thread is open. It updates the preview and the unread badge in the list
   * without refetching the whole inbox — a refetch per message turns a busy
   * channel into a request per message per participant.
   */
  useEffect(() => {
    if (!socket) return undefined

    const onInbox = ({ message, room }) => {
      const idFromRoom = Number(String(room || "").split(":")[1])
      if (!Number.isFinite(idFromRoom)) return

      setRooms((current) => {
        const index = current.findIndex((r) => Number(r.id) === idFromRoom)
        if (index === -1) {
          // A thread this tab has never seen — somebody just added them to it.
          loadRooms()
          return current
        }
        const isOpen = String(activeRoomRef.current) === String(idFromRoom)
        const updated = {
          ...current[index],
          last_message_at: message.created_at,
          last_message_preview: message.type === "file" ? `📎 ${message.body}` : message.body,
          unread_count: isOpen ? 0 : (current[index].unread_count || 0) + 1,
        }
        // Moved to the top: a thread that just received a message belongs there,
        // and re-sorting the whole list on every message is wasted work.
        return [updated, ...current.slice(0, index), ...current.slice(index + 1)]
      })
    }

    socket.on("message_inbox", onInbox)
    return () => socket.off("message_inbox", onInbox)
  }, [socket, loadRooms])

  /* Join the open thread's room, and leave it on the way out. Tracked, so a
     reconnect replays the join — otherwise the tab sits silently outside its own
     thread until the user navigates away and back. */
  useEffect(() => {
    if (!roomId) return undefined
    const room = rooms.find((r) => String(r.id) === String(roomId))
    if (!room?.socket_room) return undefined

    joinRoomTracked(room.socket_room)
    return () => leaveRoomTracked(room.socket_room)
  }, [roomId, rooms])

  /*
   * Internal, or with a client?
   *
   * Organization 0 is the back office and a tenant like any other, so a room
   * belonging to it is one of ours. A direct message is decided by who is on the
   * other end rather than by the room's client id — a DM is a conversation
   * between two people, and which tenant the row was filed under says nothing
   * about whether it leaves the building.
   */
  const isInternal = useCallback(
    (room) => (room.kind === "dm" ? String(room.peer?.organization_id) === "0" : String(room.client_id) === "0"),
    [],
  )

  /* Every client that actually appears in the list. Built from the rooms rather
     than from the client registry: a filter offering forty clients you have no
     conversations with is forty ways to empty the list. */
  const clientsInView = useMemo(() => {
    const seen = new Map()
    for (const room of rooms) {
      if (isInternal(room)) continue
      const id = room.kind === "dm" ? room.peer?.organization_id : room.client_id
      const label = room.kind === "dm" ? room.peer?.organization_label || room.client_label : room.client_label
      if (id != null && !seen.has(String(id))) seen.set(String(id), label || `Client ${id}`)
    }
    return [...seen.entries()].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label))
  }, [rooms, isInternal])

  const filtered = useMemo(() => {
    let list = rooms
    if (filter === "unread") list = list.filter((r) => (r.unread_count || 0) > 0)
    else if (filter === "internal") list = list.filter(isInternal)
    else if (filter === "clients") list = list.filter((r) => !isInternal(r))
    else if (filter !== "all") list = list.filter((r) => r.kind === filter)

    /* The client filter narrows whatever tab is open rather than replacing it,
       so "unread, from Northwind" is one thought rather than two screens. */
    if (clientFilter) {
      list = list.filter((r) => String(r.kind === "dm" ? r.peer?.organization_id : r.client_id) === clientFilter)
    }
    return list
  }, [rooms, filter, clientFilter, isInternal])

  const unreadRooms = useMemo(() => rooms.filter((r) => (r.unread_count || 0) > 0).length, [rooms])

  const markRoomRead = useCallback((id) => {
    setRooms((current) => current.map((r) => (String(r.id) === String(id) ? { ...r, unread_count: 0 } : r)))
  }, [])

  return (
    <Box>
      <PageHeader
        title="Chat"
        subtitle="Direct messages, and named topic threads with clients that can carry a project and a task."
        actions={
          can("chat", "create") ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
              New conversation
            </Button>
          ) : null
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {/* Measured, not guessed. `calc(100vh - 230px)` was right on the day it
          was written and wrong the moment a timer bar or a warning banner
          appeared above it — see useFillViewport. */}
      <Stack
        ref={frameRef}
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems="stretch"
        sx={{ height: { md: frameHeight || "calc(100vh - 230px)" } }}
      >
        <Card sx={{ width: { xs: "100%", md: 330 }, flexShrink: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Tabs
            value={filter}
            onChange={(_, value) => setFilter(value)}
            variant="scrollable"
            scrollButtons={false}
            sx={{ borderBottom: 1, borderColor: "divider", minHeight: 42, "& .MuiTab-root": { minHeight: 42, fontSize: 13 } }}
          >
            <Tab value="all" label="All" />
            {/* The count is the whole reason to look at this tab. A bare word
                means opening it to find out whether it holds anything — which is
                exactly the trip the tab exists to save. Rooms, not messages:
                "Unread · 3" is three conversations to answer. */}
            <Tab value="unread" label={unreadRooms > 0 ? `Unread · ${unreadRooms}` : "Unread"} />
            {/* Who the conversation is WITH, which is the division that actually
                matters here — everything a client can read versus everything
                that stays inside. A client sees neither tab: they have one
                organization, so the split would be every thread on one side. */}
            {isBackOffice ? <Tab value="clients" label="Clients" /> : null}
            {isBackOffice ? <Tab value="internal" label="Internal" /> : null}
            <Tab value="topic" label="Topics" />
            <Tab value="dm" label="Direct" />
            {isBackOffice ? <Tab value="support" label="Support" /> : null}
          </Tabs>

          {/* Only where there is a choice to make. One client is not a filter. */}
          {isBackOffice && clientsInView.length > 1 ? (
            <Box sx={{ px: 1.25, pt: 1.25 }}>
              <ClientSelect
                clients={clientsInView}
                value={clientFilter}
                onChange={setClientFilter}
                emptyLabel="All clients"
                fullWidth
              />
            </Box>
          ) : null}

          <ChatRoomList
            rooms={filtered}
            loading={loading}
            activeId={roomId}
            filter={filter}
            onClearFilter={() => {
              setFilter("all")
              setClientFilter("")
            }}
            onSelect={(id) => navigate(`/chat/${id}`)}
            onCreate={can("chat", "create") ? () => setNewOpen(true) : null}
          />
        </Card>

        <Card sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
          <ChatThread roomId={roomId} onRead={markRoomRead} onRoomChanged={loadRooms} />
        </Card>
      </Stack>

      <NewConversationDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(room) => {
          setNewOpen(false)
          loadRooms()
          if (room?.id) navigate(`/chat/${room.id}`)
        }}
      />
    </Box>
  )
}
